import type { NextResponse } from 'next/server'
import { catalogAmount } from '@/lib/service-catalog'

export const HB_PLAYBOOK_PRODUCT = {
  id: 'hb-playbook',
  sku: 'hb-playbook-inquiry-to-contract',
  slug: 'home-builders',
  name: 'Inquiry to Contract - The Home Builder Pipeline Playbook',
  description:
    'PDF, templates, CRM packs, and Playbook Agent access. One-time purchase.',
  price: catalogAmount('hb-playbook'),
} as const

export const HB_PLAYBOOK_BUY_PATH = '/services/home-builders/playbook'
export const HB_PLAYBOOK_PATH = '/services/home-builders/playbook/download'
export const HB_PLAYBOOK_LEGACY_PATH = '/playbooks/inquiry-to-contract'

export const HB_PLAYBOOK_ACCESS = {
  cookie: 'hb_playbook',
  previewParam: 'preview',
  previewValue: '1',
  /** Permanent per-purchase access key, emailed to the buyer. */
  keyParam: 'k',
  path: HB_PLAYBOOK_PATH,
  cookiePath: HB_PLAYBOOK_PATH,
  // Older emails and bookmarks used this cookie path. Middleware still
  // honours it on the legacy URL, then reissues the cookie on cookiePath.
  legacyCookiePath: '/playbooks',
  // Browsers cap persistent cookies near 400 days. The emailed key link is the
  // credential that never expires; the cookie only saves the buyer a click.
  maxAge: 60 * 60 * 24 * 400,
} as const

/** Stripe Checkout custom field that captures the buyer's website. */
export const HB_PLAYBOOK_WEBSITE_FIELD = 'website'

export function isInquiryToContractPlaybookPath(pathname: string): boolean {
  return isPlaybookDownloadPath(pathname) || isLegacyPlaybookPath(pathname)
}

export function isPlaybookDownloadPath(pathname: string): boolean {
  return pathname === HB_PLAYBOOK_PATH || pathname.startsWith(`${HB_PLAYBOOK_PATH}/`)
}

export function isLegacyPlaybookPath(pathname: string): boolean {
  return pathname === HB_PLAYBOOK_LEGACY_PATH || pathname.startsWith(`${HB_PLAYBOOK_LEGACY_PATH}/`)
}

/** Map a legacy /playbooks/... request onto the public download URL. */
export function modernPlaybookPath(pathname: string): string {
  if (!isLegacyPlaybookPath(pathname)) return pathname
  return `${HB_PLAYBOOK_PATH}${pathname.slice(HB_PLAYBOOK_LEGACY_PATH.length)}`
}

export function playbookUnlockPath(sessionId: string): string {
  return `/api/playbooks/unlock?session_id=${encodeURIComponent(sessionId)}`
}

export function playbookAccessTokenForSession(sessionId: string): Promise<string> {
  return signPlaybookAccess(`paid:${sessionId}`)
}

export function playbookAccessUrl(origin: string, token: string): string {
  return `${origin}${HB_PLAYBOOK_PATH}?${HB_PLAYBOOK_ACCESS.keyParam}=${encodeURIComponent(token)}`
}

export function isPaidPlaybookSession(session: {
  payment_status?: string | null
  metadata?: Record<string, string> | null
}): boolean {
  return (
    session.payment_status === 'paid' &&
    session.metadata?.type === 'service_booking' &&
    session.metadata?.productId === HB_PLAYBOOK_PRODUCT.id
  )
}

function accessSecret(): string {
  return process.env.PLAYBOOK_ACCESS_SECRET || process.env.STRIPE_SECRET_KEY || 'local-playbook-preview'
}

async function hmacHex(value: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(accessSecret()),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(value))
  return [...new Uint8Array(sig)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export async function signPlaybookAccess(value: string): Promise<string> {
  return `${value}.${await hmacHex(value)}`
}

export async function verifyPlaybookAccess(token: string | undefined): Promise<string | null> {
  if (!token) return null
  const i = token.lastIndexOf('.')
  if (i <= 0) return null
  const value = token.slice(0, i)
  const sig = token.slice(i + 1)
  const expected = await hmacHex(value)
  if (sig.length !== expected.length) return null
  let mismatch = 0
  for (let j = 0; j < sig.length; j++) mismatch |= sig.charCodeAt(j) ^ expected.charCodeAt(j)
  return mismatch === 0 ? value : null
}

export function applyPlaybookAccessCookie(res: NextResponse, token: string, secure: boolean) {
  res.cookies.set(HB_PLAYBOOK_ACCESS.cookie, token, {
    httpOnly: true,
    sameSite: 'lax',
    secure,
    path: HB_PLAYBOOK_ACCESS.cookiePath,
    maxAge: HB_PLAYBOOK_ACCESS.maxAge,
  })
}

function isLocalHost(hostHeader: string): boolean {
  const host = hostHeader.toLowerCase().split(':')[0]
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.localhost')
}

export function playbookCookieShouldBeSecure(hostHeader: string): boolean {
  return !isLocalHost(hostHeader)
}

/**
 * Origin for links inside transactional email. Stripe webhooks forwarded to a
 * local tunnel arrive with a localhost host, so test purchases get test links.
 */
export function playbookEmailOrigin(hostHeader: string, productionOrigin: string): string {
  if (hostHeader && isLocalHost(hostHeader)) return `http://${hostHeader}`
  return productionOrigin
}
